/**
 * JOY-82 — Gift With Purchase logic
 *
 * Scope: the Hard+ 3-month subscription A/B/C test (Linear JOY-82).
 *
 * Pricing is handled server-side by a Shopify automatic discount — this JS
 * only manages gift PRESENCE in the cart. The discount zeroes gift line
 * prices when the trigger (Daily Jar / 3 + a Variant B/C selling plan) is
 * in the cart.
 *
 * Lifecycle:
 *   - On product-form `submit` (capture phase): if the submission matches the
 *                             trigger, intercept and bulk-add Hard+ + gifts
 *                             in a single /cart/add.js request so everything
 *                             lands in the drawer at once.
 *   - On cart drawer quantity-minus `click` (capture phase): if the click
 *                             would take the trigger to 0, intercept and
 *                             bulk-zero Hard+ + all gift lines in a single
 *                             /cart/update.js so everything disappears from
 *                             the drawer at once.
 *   - On `cart:item-added`:   safety net. If the trigger lands in the cart
 *                             via any other path (bundle flow, abandoned
 *                             cart recovery, deep link), top up the missing
 *                             gifts. Dedup makes this a no-op when the
 *                             submit interceptor already handled it.
 *   - On `cart:item-removed`: safety net. If the trigger is no longer in
 *                             cart (removed by any path other than our click
 *                             interceptor — e.g. the cart page, CartJS API
 *                             call from elsewhere), clean up any remaining
 *                             gift lines.
 *
 * The `_joy82_gift` line item property is the single source of truth for
 * "this line is one of ours" — used for dedup, removal, and the cart drawer
 * UI treatment in `snippets/cart__product.liquid`. If you rename it here,
 * rename it there too.
 *
 * This file is test-scoped. When the test ends:
 *   1. Delete this file.
 *   2. Delete the `<script src="joy82-gwp.js" ...>` line in
 *      `layout/theme.liquid`.
 *   3. Grep for `_joy82_gift` and remove the UI treatment in
 *      `snippets/cart__product.liquid` (and anywhere else it turns up).
 *   4. Deactivate the two automatic discounts in Shopify admin.
 */

(() => {
  // ===========================================================================
  // Gift variant IDs — each is a dedicated variant on an unlisted "FREE ..."
  // product duplicated from the real product. Prices are the real retail
  // prices; the Shopify automatic discount (see TODO(ops) below) is what
  // zeroes them at checkout.
  // ===========================================================================
  const PRIME_T_GIFT_VARIANT_ID = 67586597552319;              // FREE Prime T — "1" variant (30 servings)
  const WATER_BOTTLE_GIFT_VARIANT_ID = 67586641297599;         // FREE JOYMODE Signature Water Bottle
  const HARD_PLUS_TRAVEL_PACK_GIFT_VARIANT_ID = 67586697429183; // FREE Hard+ — Travel 10-Pack / 1 variant (Variant C only)
  // const GUIDE_GIFT_VARIANT_ID = 67586674032831;                // FREE JOYMODE Performance Playbook (DIGITAL)

  // ===========================================================================
  // TODO(ops): create two automatic Shopify discounts, one per test variant:
  //
  //   - "JOY-82 Variant B GWP"
  //       Trigger: Daily Jar / 3 (44450578530495) + selling plan 6754271423
  //       Reward:  100% off Prime T Gift variant + Water Bottle Gift variant
  //
  //   - "JOY-82 Variant C GWP"
  //       Trigger: Daily Jar / 3 (44450578530495) + selling plan 6754304191
  //       Reward:  100% off Prime T Gift + Travel Pack Gift + Water Bottle Gift
  //
  // Confirm whether Shopify's native BXGY discount can match on selling plan
  // ID. If not, choose:
  //   (a) match on variant only (edge case: Daily Jar / 3 OTP buyers would
  //       also trigger — probably low volume), OR
  //   (b) implement via a Shopify Discount Function (needs an app).
  // ===========================================================================

  const TRIGGER_VARIANT_ID = 44450578530495; // Daily Jar / 3
  const VARIANT_B_PLAN_ID = 6754271423;
  const VARIANT_C_PLAN_ID = 6754304191;

  // The Guide (Playbook) is included in both variants per the ticket spec
  // ("only shown in cart, no PDP communication") — it's auto-added to cart
  // as a surprise and intentionally not advertised on the PDP.
  const GIFT_LISTS = {
    [VARIANT_B_PLAN_ID]: [
      PRIME_T_GIFT_VARIANT_ID,
      WATER_BOTTLE_GIFT_VARIANT_ID,
      // GUIDE_GIFT_VARIANT_ID,
    ],
    [VARIANT_C_PLAN_ID]: [
      PRIME_T_GIFT_VARIANT_ID,
      HARD_PLUS_TRAVEL_PACK_GIFT_VARIANT_ID,
      WATER_BOTTLE_GIFT_VARIANT_ID,
      // GUIDE_GIFT_VARIANT_ID,
    ],
  };

  const MARKER = '_joy82_gift';

  // Recursion guard: our own add/remove calls will fire cart:item-added /
  // cart:item-removed, which would re-enter these handlers.
  let busy = false;

  const fetchCart = () =>
    fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

  const findTriggerLine = cart =>
    cart.items.find(item =>
      item.variant_id === TRIGGER_VARIANT_ID &&
      (item.selling_plan_allocation?.selling_plan?.id === VARIANT_B_PLAN_ID ||
        item.selling_plan_allocation?.selling_plan?.id === VARIANT_C_PLAN_ID)
    );

  // Bulk-add all missing gifts in a single POST. Atomic: either all land or
  // Shopify rejects the batch with an actionable error. Beats sequential
  // adds (which paint incrementally in the drawer and silently drop
  // individual failures).
  const addGifts = async (variantIds) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: variantIds.map(id => ({
          id,
          quantity: 1,
          properties: { [MARKER]: 'true' },
        })),
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      console.error('[JOY-82] GWP bulk add rejected:', response.status, body);
      throw new Error(`cart/add rejected ${response.status}`);
    }
    return response.json();
  };

  const removeLine = lineKey =>
    fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });

  // Our /cart/add.js and /cart/change.js calls bypass CartJS entirely, so
  // they don't fire cart:item-added / cart:item-removed and they don't
  // update CartJS's cached cart. We trigger cart:refresh ourselves after any
  // of our mutations so CartJS re-fetches /cart.js and the drawer renders
  // the gifts.
  const refreshCart = () => $(document).trigger('cart:refresh');

  // Intercept the product form submit so the trigger + gifts land in a single
  // bulk /cart/add.js request. Runs in capture phase to beat CartJS's own
  // submit handler. Only intercepts when the form is actually submitting the
  // trigger with a Variant B/C selling plan — everything else (control, OTP,
  // 1-month, non-Hard+ products) falls through to the normal path unchanged.
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form || !form.matches || !form.matches('form[data-product-form]')) return;

    const variantId = Number(form.querySelector('input[name="id"]')?.value);
    const sellingPlanId = Number(form.querySelector('input[name="selling_plan"]')?.value);
    const quantity = Number(form.querySelector('input[name="quantity"]')?.value) || 1;

    if (variantId !== TRIGGER_VARIANT_ID) return;
    if (sellingPlanId !== VARIANT_B_PLAN_ID && sellingPlanId !== VARIANT_C_PLAN_ID) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    busy = true;

    try {
      const cart = await fetchCart();
      const giftList = (GIFT_LISTS[sellingPlanId] || []).filter(Boolean);
      const missingGifts = giftList.filter(giftVariantId =>
        !cart.items.some(item =>
          item.variant_id === giftVariantId &&
          item.properties?.[MARKER] === 'true'
        )
      );

      const items = [
        { id: variantId, quantity, selling_plan: sellingPlanId },
        ...missingGifts.map(id => ({
          id,
          quantity: 1,
          properties: { [MARKER]: 'true' },
        })),
      ];

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        console.error('[JOY-82] atomic ATC rejected:', response.status, body);
        return;
      }

      refreshCart();
    } catch (err) {
      console.error('[JOY-82] atomic ATC failed:', err);
    } finally {
      busy = false;
    }
  }, true);

  // Intercept the cart drawer's quantity-minus click so a decrement-to-zero
  // on the trigger line removes Hard+ + all gift lines in a single bulk
  // /cart/update.js, instead of CartJS removing Hard+ alone and leaving our
  // cart:item-removed listener to clean up gifts afterward.
  //
  // The decision whether to intercept must be synchronous (preventDefault
  // can't be called after an await), so we read CartJS's cached cart state
  // (window.CartJS.cart) rather than refetching. CartJS keeps this cache in
  // sync via getCart() after every cart:refresh — which we fire after any
  // of our own mutations — so it's reliable by the time a user is clicking.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest ? e.target.closest('[data-cart-update]') : null;
    if (!btn) return;

    const newQty = Number(btn.getAttribute('data-cart-quantity'));
    if (newQty !== 0) return;

    const line = Number(btn.getAttribute('data-cart-update'));
    const cachedCart = window.CartJS && window.CartJS.cart;
    if (!cachedCart || !cachedCart.items) return;

    const targetLine = cachedCart.items[line - 1]; // data-cart-update is 1-based
    if (!targetLine) return;

    const planId = targetLine.selling_plan_allocation &&
      targetLine.selling_plan_allocation.selling_plan &&
      targetLine.selling_plan_allocation.selling_plan.id;

    const isTrigger =
      targetLine.variant_id === TRIGGER_VARIANT_ID &&
      (planId === VARIANT_B_PLAN_ID || planId === VARIANT_C_PLAN_ID);

    if (!isTrigger) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    busy = true;

    (async () => {
      try {
        // Refetch /cart.js for authoritative line keys (CartJS's cache may
        // lag by a few hundred ms after our fetch-based mutations).
        const cart = await fetchCart();
        const triggerLineFresh = findTriggerLine(cart);
        if (!triggerLineFresh) return;

        const updates = { [triggerLineFresh.key]: 0 };
        for (const item of cart.items) {
          if (item.properties && item.properties[MARKER] === 'true') {
            updates[item.key] = 0;
          }
        }

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => null);
          console.error('[JOY-82] atomic remove rejected:', response.status, body);
          return;
        }

        refreshCart();
      } catch (err) {
        console.error('[JOY-82] atomic remove failed:', err);
      } finally {
        busy = false;
      }
    })();
  }, true);

  // Safety net: if the trigger lands in cart via a path other than the form
  // interceptor above (bundle flows, deep links, cart recovery), top up
  // missing gifts. Dedup makes this a no-op when the interceptor already
  // handled the add.
  $(document).on('cart:item-added', async () => {
    if (busy) return;
    busy = true;
    let addedAny = false;
    try {
      const cart = await fetchCart();
      const trigger = findTriggerLine(cart);
      if (!trigger) return;

      const planId = trigger.selling_plan_allocation.selling_plan.id;
      const giftList = (GIFT_LISTS[planId] || []).filter(Boolean);

      const missing = giftList.filter(giftVariantId =>
        !cart.items.some(item =>
          item.variant_id === giftVariantId &&
          item.properties?.[MARKER] === 'true'
        )
      );

      if (missing.length === 0) return;

      await addGifts(missing);
      addedAny = true;
    } catch (err) {
      console.error('[JOY-82] GWP add failed:', err);
    } finally {
      busy = false;
      if (addedAny) refreshCart();
    }
  });

  // Remove gifts when the trigger leaves the cart.
  $(document).on('cart:item-removed', async () => {
    if (busy) return;
    busy = true;
    let removedAny = false;
    try {
      const cart = await fetchCart();
      if (findTriggerLine(cart)) return; // Trigger still present → leave gifts alone.

      const giftLines = cart.items.filter(
        item => item.properties?.[MARKER] === 'true'
      );
      for (const line of giftLines) {
        await removeLine(line.key);
        removedAny = true;
      }
    } catch (err) {
      console.error('[JOY-82] GWP remove failed:', err);
    } finally {
      busy = false;
      if (removedAny) refreshCart();
    }
  });
})();
